import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { usePharmacy } from '@/context/PharmacyContext';

interface NavbarProps {
  isScrolled: boolean;
}

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'Pharmacy Services', path: '/pharmacy-services' },
  { name: 'Addiction Services', path: '/addiction-services' },
  { name: 'Blog', path: '/blog' },
  { name: 'Location', path: '/location' },
];

export default function Navbar({ isScrolled }: NavbarProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isHoursOpen, setIsHoursOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { settings } = usePharmacy();

  const handleLogoClick = () => {
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  const schedule = settings?.business_hours || [];
  const phone = settings?.phone || '(905) 709-9985';
  const phoneRaw = settings?.phone_raw || '9057099985';
  const shortAddress = settings ? `${settings.address_line1.split(' ').slice(0, 3).join(' ')}, ${settings.city}` : '8108 Yonge St, Thornhill';

  const getTodayInfo = useCallback(() => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const todayIdx = new Date().getDay();
    const todayDay = days[todayIdx];
    const todaySchedule = schedule.find((s: any) => s.day === todayDay);
    return { day: todayDay, hours: todaySchedule?.hours || 'Closed' };
  }, [schedule]);

  const todayInfo = getTodayInfo();

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const logoImg = 'https://static.readdy.ai/image/621fe86c950ded9745dd2c19cd7c5595/c0b5a15a2b815315bfd2ccbd2c453721.png';
  const pharmacyName = settings?.pharmacy_name || 'Thornhill Drug-Mart';
  const tagline = settings?.tagline || 'Addiction Treatment & Pharmacy';

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 w-full">
        {/* Top Info Bar */}
        <div
          className={`bg-background-50 border-b border-background-200 w-full transition-all duration-300 overflow-hidden ${
            isScrolled ? 'h-0 opacity-0' : 'h-14 opacity-100'
          }`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 xl:px-8 flex items-center justify-between h-14">
            {/* Desktop Logo */}
            <button
              onClick={handleLogoClick}
              className="hidden md:flex items-center gap-2.5 cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
              aria-label={`${pharmacyName} — go to homepage`}
            >
              <img
                src={logoImg}
                alt={`${pharmacyName} logo`}
                className="w-8 h-8 object-contain flex-shrink-0"
              />
              <div className="leading-tight text-left">
                <span className="block text-sm font-bold text-foreground-900 tracking-tight">
                  {pharmacyName}
                </span>
                <span className="block text-[10px] font-medium text-foreground-600 tracking-wide uppercase">
                  {tagline}
                </span>
              </div>
            </button>

            {/* Mobile: Hours */}
            <div className="flex md:hidden items-center gap-2.5">
              <div className="w-7 h-7 rounded-full border border-primary-200 flex items-center justify-center flex-shrink-0">
                <i className="ri-time-line text-primary-700 text-xs"></i>
              </div>
              <div className="leading-tight">
                <span className="block text-[10px] text-foreground-500 uppercase tracking-wide">
                  {todayInfo.day}
                </span>
                <span className="block text-sm font-semibold text-foreground-900 whitespace-nowrap">
                  {todayInfo.hours}
                </span>
              </div>
            </div>

            {/* Desktop Center Info Blocks */}
            <div className="hidden md:flex items-center gap-6 lg:gap-8">
              {/* Phone */}
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full border border-primary-300/40 flex items-center justify-center flex-shrink-0">
                  <i className="ri-phone-line text-primary-500 text-sm"></i>
                </div>
                <div className="leading-tight">
                  <span className="block text-[10px] text-foreground-500 uppercase tracking-wide">
                    Call Us Today
                  </span>
                  <a
                    href={`tel:${phoneRaw}`}
                    className="block text-sm font-semibold text-foreground-900 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {phone}
                  </a>
                </div>
              </div>

              {/* Hours */}
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full border border-foreground-200 flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-foreground-600 text-sm"></i>
                </div>
                <div className="leading-tight">
                  <span className="block text-[10px] text-foreground-500 uppercase tracking-wide">
                    {todayInfo.day}
                  </span>
                  <span className="block text-sm font-semibold text-foreground-900 whitespace-nowrap">
                    {todayInfo.hours}
                  </span>
                </div>
              </div>
            </div>

            {/* Desktop CTA */}
            <a
              href={`tel:${phoneRaw}`}
              className="hidden md:inline-flex bg-primary-500 hover:bg-primary-600 text-background-50 text-xs font-semibold px-5 py-2 rounded-full transition-colors whitespace-nowrap cursor-pointer items-center gap-1.5"
            >
              Get Help Now
            </a>

            {/* Mobile: Call Button */}
            <a
              href={`tel:${phoneRaw}`}
              className="flex md:hidden items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-xs font-semibold px-4 py-2 rounded-full transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line"></i>
              <span>Call</span>
            </a>
          </div>
        </div>

        {/* Bottom Nav Bar */}
        <nav
          className={`bg-primary-800 w-full transition-shadow duration-300 ${
            isScrolled ? 'shadow-md' : ''
          }`}
          aria-label="Main navigation"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 xl:px-8">
            <div className="flex items-center justify-between h-12">
              {/* Desktop Nav Links */}
              <div className="hidden lg:flex items-center gap-1">
                {navLinks.map((link) => (
                  <a
                    key={link.name}
                    href={link.path}
                    className="text-xs font-medium text-white/80 hover:text-white px-3 py-2 rounded-md hover:bg-white/10 transition-all whitespace-nowrap cursor-pointer uppercase tracking-wide"
                  >
                    {link.name}
                  </a>
                ))}
              </div>

              {/* Mobile: Logo on dark bar */}
              <button
                onClick={handleLogoClick}
                className="lg:hidden flex items-center gap-2 cursor-pointer"
                aria-label={`${pharmacyName} — go to homepage`}
              >
                <img
                  src={logoImg}
                  alt={`${pharmacyName} logo`}
                  className="w-7 h-7 object-contain"
                />
                <span className="text-xs font-bold text-white">
                  {pharmacyName}
                </span>
              </button>

              {/* Right side: social + address */}
              <div className="hidden lg:flex items-center gap-5">
                <div className="flex items-center gap-2">
                  <a
                    href="#"
                    className="w-6 h-6 flex items-center justify-center text-white/50 hover:text-white transition-colors cursor-pointer"
                    aria-label="Facebook"
                  >
                    <i className="ri-facebook-fill text-sm"></i>
                  </a>
                  <a
                    href="#"
                    className="w-6 h-6 flex items-center justify-center text-white/50 hover:text-white transition-colors cursor-pointer"
                    aria-label="Instagram"
                  >
                    <i className="ri-instagram-line text-sm"></i>
                  </a>
                  <a
                    href="#"
                    className="w-6 h-6 flex items-center justify-center text-white/50 hover:text-white transition-colors cursor-pointer"
                    aria-label="Twitter / X"
                  >
                    <i className="ri-twitter-x-line text-sm"></i>
                  </a>
                </div>
                <div className="flex items-center gap-1.5 text-white/50 text-xs">
                  <i className="ri-map-pin-line text-xs"></i>
                  <span className="whitespace-nowrap">
                    {shortAddress}
                  </span>
                </div>
              </div>

              {/* Mobile hamburger on dark bar */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden w-8 h-8 flex items-center justify-center cursor-pointer flex-shrink-0"
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={isMobileMenuOpen}
              >
                <i
                  className={`${
                    isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'
                  } text-xl text-white`}
                ></i>
              </button>
            </div>
          </div>

          {/* Mobile Dropdown */}
          {isMobileMenuOpen && (
            <div className="lg:hidden bg-primary-800 border-t border-white/10 w-full max-h-[calc(100vh-112px)] overflow-y-auto">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 space-y-1">
                {/* Mobile Info Blocks */}
                <div className="flex flex-col gap-3 pb-4 mb-2 border-b border-white/10">
                  <a
                    href={`tel:${phoneRaw}`}
                    className="flex items-center gap-3 text-white/80 hover:text-white transition-colors cursor-pointer"
                  >
                    <div className="w-8 h-8 rounded-full border border-primary-400/30 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-primary-400 text-sm"></i>
                    </div>
                    <div className="leading-tight">
                      <span className="block text-[10px] text-white/50 uppercase tracking-wide">
                        Call Us Today
                      </span>
                      <span className="block text-sm font-semibold">
                        {phone}
                      </span>
                    </div>
                  </a>

                  <button
                    onClick={() => setIsHoursOpen(!isHoursOpen)}
                    className="flex items-center gap-3 text-white/80 hover:text-white transition-colors cursor-pointer text-left w-full"
                  >
                    <div className="w-8 h-8 rounded-full border border-white/20 flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-white/70 text-sm"></i>
                    </div>
                    <div className="leading-tight flex-1">
                      <span className="block text-[10px] text-white/50 uppercase tracking-wide">
                        {todayInfo.day}
                      </span>
                      <div className="flex items-center justify-between">
                        <span className="block text-sm font-semibold">
                          {todayInfo.hours}
                        </span>
                        <i
                          className={`ri-arrow-down-s-line text-sm transition-transform ${isHoursOpen ? 'rotate-180' : ''}`}
                        ></i>
                      </div>
                    </div>
                  </button>

                  {/* Expandable hours */}
                  {isHoursOpen && (
                    <div className="ml-11 space-y-1 animate-slideDown">
                      {schedule.map((item: any) => (
                        <div
                          key={item.day}
                          className={`flex items-center justify-between py-1.5 px-2 rounded ${
                            todayInfo.day === item.day
                              ? 'bg-white/10'
                              : ''
                          }`}
                        >
                          <span className="text-xs text-white/70">
                            {item.day}
                          </span>
                          <span className="text-xs text-white/70">
                            {item.hours}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  <a
                    href={`tel:${phoneRaw}`}
                    className="flex items-center justify-center gap-2 w-full bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-3 rounded-full font-semibold transition-colors cursor-pointer mt-1"
                  >
                    <i className="ri-phone-line text-lg"></i>
                    <span>Get Help Now</span>
                  </a>
                </div>

                {/* Mobile Nav Links */}
                {navLinks.map((link, index) => (
                  <a
                    key={link.name}
                    href={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="flex items-center space-x-3 px-4 py-3 rounded-lg text-white/80 hover:bg-white/10 hover:text-white font-medium transition-all cursor-pointer group"
                  >
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 flex-shrink-0">
                      <i
                        className={`${
                          index === 0
                            ? 'ri-home-line'
                            : index === 1
                            ? 'ri-capsule-line'
                            : index === 2
                            ? 'ri-heart-pulse-line'
                            : index === 3
                            ? 'ri-article-line'
                            : 'ri-map-pin-line'
                        } text-base text-white/70`}
                      ></i>
                    </div>
                    <span>{link.name}</span>
                  </a>
                ))}
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Spacer for fixed header */}
      <div
        className={`w-full transition-all duration-300 ${
          isScrolled ? 'h-12' : 'h-[104px]'
        }`}
      ></div>
    </>
  );
}